import { ArrowRight, ButtonLink } from "@/components/ui/Button";
import { JsonLd } from "@/components/ui/JsonLd";
import { MediaImage } from "@/components/ui/MediaImage";
import { Reveal, RevealGroup } from "@/components/ui/Reveal";
import { Section } from "@/components/ui/Section";
import { VideoBlock } from "@/components/ui/VideoBlock";
import { visibleItems, visibleLinks } from "@/components/cms/SectionRenderer";
import { useMediaByKey } from "@/content-context";
import type { SectionDoc } from "@/content";
import { imageReveal } from "@/lib/motion";
import { videoJsonLd } from "@/lib/jsonld";
import { parseVideoUrl } from "@/lib/video";

/**
 * "Why HISCUS", which replaced "Documented work in".
 *
 * The band it replaced scrolled seven place names past the reader behind a mask
 * and told them nothing they could act on. A funder or an institutional partner
 * arrives with one question at that point on the page: who is this
 * organisation, and why would I work with it rather than somebody else. This
 * section answers it in about eighty words, four checkable trust points and a
 * picture, and then offers the two next steps.
 *
 * Every claim in it is already evidenced elsewhere on this site: the
 * registration year on Transparency, the Bethanchowk sequence on the project
 * page, the reporting periods on Impact. Nothing here is a new assertion.
 *
 * The video is the film supplied directly by HISCUS. VideoBlock keeps it
 * click-to-load and uses youtube-nocookie, so no third-party request is made
 * merely because somebody opened the homepage.
 *
 * Heading, lead, body, trust points, image, video URL and both buttons are all
 * editable from the admin panel, with the committed copy below as the fallback.
 */

const FALLBACK_POINTS = [
  {
    title: "Nepali-founded, Nepali-run",
    text: "Registered in Nepal in 1998, with the District Administration Office and the Social Welfare Council.",
  },
  {
    title: "Locally led, and asked for",
    text: "Every programme begins with a request from the ward, school, health post or group that will host it.",
  },
  {
    title: "Something durable left behind",
    text: "Relief work is expected to leave a tank, a path or a wall, not only a distribution day.",
  },
  {
    title: "Evidence, with its period",
    text: "Every figure on this site carries the reporting period it refers to.",
  },
];

const FALLBACK_BODY = [
  "HISCUS was founded and is run by Nepalis, in Nepal. Programmes start with a request from a ward, a school, a health post or a community group, and are planned with the people who will host them.",
  "The organisation works in a small number of places repeatedly rather than in many places once. In Bethanchowk that meant a health post, a women's session and a water tank inside nine months, and the tank is still there.",
];

export function WhyHiscus({ section }: { section: SectionDoc | null }) {
  const byKey = useMediaByKey();

  const eyebrow = section?.eyebrow || "Who we are";
  const title = section?.title || "Why HISCUS";
  const lead =
    section?.lead ||
    "A Nepali organisation, not a country office. Registered in 1998, working with the same communities long enough to be held to what it said it would do.";

  const paragraphs = section?.body?.length
    ? section.body.filter((b) => b.kind === "paragraph" && b.text).map((b) => b.text as string)
    : FALLBACK_BODY;

  const points = section?.items?.length
    ? visibleItems(section).map((item) => ({ title: item.title ?? "", text: item.text ?? "" }))
    : FALLBACK_POINTS;

  const poster = byKey(section?.posterMediaKey) ?? byKey("ct-welcome");
  const video = parseVideoUrl(section?.videoUrl);
  const videoTitle = section?.videoTitle || `${title}: HISCUS in the communities it works with`;

  const ctas = section?.ctas?.length
    ? visibleLinks(section.ctas)
    : [
        { label: "About HISCUS", href: "/about" },
        { label: "Support our work", href: "/get-involved/support" },
      ];

  return (
    <Section tone="white" ariaLabelledBy="why-hiscus-heading">
      <div className="grid items-start gap-9 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:gap-16">
        <Reveal>
          <p className="eyebrow">{eyebrow}</p>
          <h2 id="why-hiscus-heading" className="mt-4 max-w-xl" style={{ fontSize: "var(--step-h2)" }}>
            {title}
          </h2>
          <p className="mt-5 max-w-xl text-muted" style={{ fontSize: "var(--step-lead)" }}>
            {lead}
          </p>

          <div className="prose-body mt-6 max-w-xl text-ink">
            {paragraphs.map((text) => (
              <p key={text.slice(0, 40)}>{text}</p>
            ))}
          </div>
        </Reveal>

        <Reveal variants={imageReveal} className="lg:pt-1">
          <VideoBlock
            url={section?.videoUrl}
            poster={poster}
            title={videoTitle}
            emptyNote=""
          />

          {section?.mediaKey ? (
            <div className="mt-5">
              <MediaImage
                item={byKey(section.mediaKey)}
                sizes="(min-width: 1024px) 42vw, 100vw"
                className="w-full rounded-[20px]"
                style={{ height: "auto" }}
              />
            </div>
          ) : null}

        </Reveal>
      </div>

      {/*
        These points span the section instead of extending only the left
        column. That keeps the video and the copy visually balanced at the top
        and avoids leaving a tall empty area beneath the player.
      */}
      <RevealGroup as="ul" className="mt-10 grid gap-x-8 gap-y-6 sm:grid-cols-2 lg:grid-cols-4">
        {points.slice(0, 4).map((point) => (
          <Reveal as="li" key={point.title}>
            <div className="h-full border-t border-line-strong/70 pt-4">
              <h3 className="flex items-start gap-2.5" style={{ fontSize: "var(--step-h4)" }}>
                <Tick />
                {point.title}
              </h3>
              <p className="mt-2 pl-[1.625rem] text-[0.9375rem] leading-relaxed text-muted">
                {point.text}
              </p>
            </div>
          </Reveal>
        ))}
      </RevealGroup>

      <div className="mt-9 flex flex-col gap-3 sm:flex-row">
        {ctas.map((cta, index) => (
          <ButtonLink
            key={cta.href + cta.label}
            href={cta.href}
            variant={index === 0 ? "primary" : "secondary"}
          >
            {cta.label}
            {index === 0 ? <ArrowRight /> : null}
          </ButtonLink>
        ))}
      </div>
      {video && section?.videoUploadDate ? (
        <JsonLd
          data={videoJsonLd({
            name: videoTitle,
            description: lead,
            uploadDate: section.videoUploadDate,
            embedUrl: video.embedUrl,
            watchUrl: video.watchUrl,
            thumbnailUrl:
              video.kind === "youtube" ? `https://i.ytimg.com/vi/${video.id}/maxresdefault.jpg` : undefined,
          })}
        />
      ) : null}
    </Section>
  );
}

/** A small mark, not an icon set. One shape, drawn once, at text size. */
function Tick() {
  return (
    <span
      aria-hidden="true"
      className="mt-[0.3em] flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-sky/45 text-navy"
    >
      <svg viewBox="0 0 16 16" className="h-2.5 w-2.5">
        <path
          d="M3.5 8.5 6.5 11.5 12.5 4.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}
